import { CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/solid';
import React, { useEffect, useState, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { client } from '../client';
import { DashHeader, Footer, LoadingOverlay, Signin, Toast, PricingSummary, PricingReviewModal, StripePaymentModal } from '../components';
import { useAuth } from '../contexts/AuthContext';
import { creditsQuery } from '../utils/Data';
import blockTools, { normalizeBlock } from '@sanity/block-tools';
import Schema from '@sanity/schema';
import axios from 'axios';
import { clientNoCdn } from '../clientNoCdn';
import { formatTitle } from '../utils/formatTitle';
import { PROVINCES } from '../utils/provinces';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC);

// TODO POST JOB LAMBDA FUNCTION
// CREATE A PUBLISHED STATE

function Postjob() {
  const { currentUser, signout } = useAuth();
  const navigate = useNavigate();
  const [credits, setCredits] = useState(0);
  const [legacyBalance, setLegacyBalance] = useState(0);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showJobBoardToast, setShowJobBoardToast] = useState(false);

  //   SIGNOUT HANDLE
  function handleSignout(e) {
    try {
      signout();
      navigate('/');
    } catch {
      setShowErrorModal(true);
    }
  }

  //   States for elements.

  // Page states
  const [defaultState, setDefaultState] = useState(true);
  const [successState, setSuccessState] = useState(false);
  const [navigatingToDashboard, setNavigatingToDashboard] = useState(false);

  //   Post Another click function
  function postAnother() {
    setDefaultState(true);
    setSuccessState(false);
  }

  // Handle dashboard navigation with delay
  function handleGoToDashboard() {
    setNavigatingToDashboard(true);
    // Wait 2 seconds for Sanity CDN to propagate
    setTimeout(() => {
      navigate('/dashboard');
    }, 2000);
  }

  // ***************Handle Post Submit************************
  const [title, setTitle] = useState('');

  const [salary, setSalary] = useState('');
  const [location, setLocation] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [vacancy, setVacancy] = useState('');
  const [body, setBody] = useState('');
  const [companyBio, setCompanyBio] = useState('');
  const [loading, setLoading] = useState(true);
  const [duration, setDuration] = useState(91);
  const [selectedBoards, setSelectedBoards] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [showPaymentErrorToast, setShowPaymentErrorToast] = useState(false);

  //   Post At IDs
  const newJobs = document.getElementById('NewJL');
  const youthJobs = document.getElementById('YouthJL');
  const ableJobs = document.getElementById('AbleJL');
  const refugeeJobs = document.getElementById('RefugeeJL');
  const aboriginalJobs = document.getElementById('AboriginalJL');
  // Employment type lives in React state (QUAL-008). It used to be read from a DOM node
  // captured at render time, which could be stale after payment and saved an empty category.
  const [category, setCategory] = useState('Full-time');

  //   random id generator
  function uuidv4() {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
      (
        c ^
        (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
      ).toString(16)
    );
  }
  // get current Date
  function currentDate() {
    return new Date().toISOString().slice(0, 10);
  }

  // get expiry Date
  function expiryDate(days) {
    let today = new Date();
    return new Date(today.getTime() + (days + 1) * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
  }

  //   Normalize and store body blocks
  // Push Block contents************************************
  const defaultSchema = Schema.compile({
    name: 'myBlog',
    types: [
      {
        type: 'object',
        name: 'blogPost',
        fields: [
          {
            title: 'Title',
            type: 'string',
            name: 'title',
          },
          {
            title: 'Body',
            name: 'body',
            type: 'array',
            of: [{ type: 'block' }],
          },
        ],
      },
    ],
  });

  const blockContentType = defaultSchema
    .get('blogPost')
    .fields.find((field) => field.name === 'body').type;
  // ***********************************************

  function handlePostSubmit(e) {
    e.preventDefault();

    // Validate at least one job board is selected
    if (selectedBoards.length < 1) {
      setShowJobBoardToast(true);
      return;
    }

    // Show review modal instead of posting immediately
    setShowReviewModal(true);
  }

  async function handleProceedToPayment() {
    const PRICING = { 91: 30, 181: 50, 365: 60 };
    const pricePerBoard = PRICING[duration];
    const boardCount = selectedBoards.length;
    const subtotal = boardCount * pricePerBoard;
    const discount = boardCount >= 2 ? subtotal * 0.10 : 0;
    const afterDiscount = subtotal - discount;
    const balanceApplied = Math.min(legacyBalance, afterDiscount);
    const total = Math.max(0, afterDiscount - balanceApplied);

    if (total === 0) {
      // Publish directly if no payment needed (fully covered by legacy balance)
      await publishJob();
    } else {
      // Create payment intent (with the job draft) and open Stripe payment modal
      try {
        const post = buildPostDoc();
        draftPostRef.current = post;
        const response = await axios.post('/.netlify/functions/createPaymentIntent', {
          post,
          amount: total,
          employerId: currentUser.uid,
          jobData: { title: formatTitle(title), companyName },
          selectedBoards,
          duration,
          legacyBalance
        });

        setClientSecret(response.data.clientSecret);
        setShowReviewModal(false);
        setShowPaymentModal(true);
      } catch (error) {
        console.error('Error creating payment intent:', error);
        setShowPaymentErrorToast(true);
      }
    }
  }

  async function handlePaymentSuccess(paymentIntent) {
    setShowPaymentModal(false);
    await publishJob(paymentIntent.id);
  }

  // The job document is built once. For card payments it's built before paying and sent with the
  // payment request, so the server can publish it from the Stripe webhook even if this tab closes
  // (SEC-004). Publishing after payment reuses the same document (same _id).
  const draftPostRef = useRef(null);

  function buildPostDoc() {
    const postAt = selectedBoards;
    // Body blocks
    const bodyBlocks = blockTools.htmlToBlocks(body, blockContentType);
    const bioBlocks = blockTools.htmlToBlocks(companyBio, blockContentType);
    const readyBodyBlocks = normalizeBlock(bodyBlocks, {
      allowedDecorators: ['strong', 'em', 'underline'],
    });
    const readyBioBlocks = normalizeBlock(bioBlocks, {
      allowedDecorators: ['strong', 'em', 'underline'],
    });

    const cityFormatted = city.toLowerCase();

    // post document
    return {
      _id: uuidv4(),
      _type: 'post',
      location: location,
      companyName: companyName,
      companyBio: readyBioBlocks,
      vacancies: vacancy > 0 ? Number(vacancy) : 1,
      categories: category,
      title: formatTitle(title),
      salary: salary,
      city: cityFormatted[0].toUpperCase() + cityFormatted.substring(1),
      province: province,
      email: email,
      body: readyBodyBlocks,
      employer: {
        _ref: currentUser.uid,
        _type: 'reference',
      },
      postedOn: currentDate(),
      postAt: postAt,
      postExpiry: expiryDate(duration),
      isActive: true,
    };
  }

  async function publishJob(paymentIntentId = null) {
    setLoading(true);
    setDefaultState(false);
    setShowReviewModal(false);

    let postAt = selectedBoards;

    if (postAt.length < 1) {
      setShowJobBoardToast(true);
      setLoading(false);
      return;
    }

    // Calculate pricing
    const PRICING = { 91: 30, 181: 50, 365: 60 };
    const pricePerBoard = PRICING[duration];
    const boardCount = selectedBoards.length;
    const subtotal = boardCount * pricePerBoard;
    const discount = boardCount >= 2 ? subtotal * 0.10 : 0;
    const afterDiscount = subtotal - discount;
    const balanceApplied = Math.min(legacyBalance, afterDiscount);

    const post = (paymentIntentId && draftPostRef.current) || buildPostDoc();

    // Create productId: {jobTitle}-{duration}-{numberOfBoards}
    const productId = `${formatTitle(title).replace(/\s+/g, '-')}-${duration}-${boardCount}`;

    // Only decrement credits if user has enough, otherwise drain remaining credits to 0
    const creditsToUse = credits - boardCount >= 0 ? boardCount : credits;

    // Call unified processing function
    try {
      await axios.post('/.netlify/functions/processPaymentAndCreatePost', {
        post,
        employerId: currentUser.uid,
        creditsUsed: creditsToUse,
        legacyBalanceUsed: balanceApplied,
        paymentIntentId,
        productId
      });

      setSuccessState(true);
      setLoading(false);
    } catch (error) {
      console.error('Error creating post:', error);
      setLoading(false);
      setShowPaymentErrorToast(true);
    }
  }

  // *********************************************

  //   fetch current available post credits
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    clientNoCdn
      .fetch(creditsQuery(currentUser.uid))
      .then((res) => {
        if (res) {
          setCredits(res[0].postCredits || 0);
          setLegacyBalance(res[0].legacyBalance || 0);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [successState, currentUser]);

  if (!currentUser && !loading) {
    return <Navigate to="/sign-in" replace />;
  }

  if (loading) {
    return <LoadingOverlay />;
  }

  if (!currentUser) {
    return <Navigate to="/sign-in" replace />;
  } else {
    return (
      <>
        <DashHeader handle={handleSignout} userEmail={currentUser?.email} />

        {/* Default post state */}
        {defaultState && (
          <>
            {legacyBalance > 0 && (
              <div className="px-4 mx-4 flex flex-col sm:flex-row justify-center lg:justify-end items-center mt-4 mb-8">
                <div className="flex items-center align-center gap-1">
                  <p className="mr-0 mb-0 font-semibold  text-gray-800">
                    Account Balance:{' '}
                    <span className="font-bold border-2 text-lg border-primary rounded px-4 py-1 text-primary ">
                      ${legacyBalance.toFixed(2)}
                    </span>
                  </p>
                  <div className="relative group">
                    <InformationCircleIcon className="h-6 w-6 text-primary cursor-help" />
                    <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-64 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-10">
                      <p>This balance will be automatically applied to your job postings. Any remaining amount will be charged via card payment.</p>
                      <div className="absolute top-full right-4 -mt-2 border-8 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="sm:px-4 sm:mx-4 xl:px-32 xl:mx-32 py-2 sm:py-16 ">
              {/* Location Information */}
              <div className="mt-10 sm:mt-0">
                <div className="xl:grid xl:grid-cols-3 xl:gap-6">
                  <div className="md:col-span-1">
                    <div className="px-4 sm:px-0">
                      <h3 className="text-lg text-center xl:text-left font-bold text-primary leading-6 ">
                        Post a Job
                      </h3>
                      <p className="mt-1 text-center xl:text-left text-sm text-gray-900">
                        Fill out the details below to post your job across multiple boards.
                      </p>
                    </div>
                  </div>
                  <div className="mt-5 md:mt-0 md:col-span-2">
                    <form onSubmit={handlePostSubmit}>
                      <div className="shadow overflow-hidden sm:rounded-md">
                        <div className="px-4 py-5 bg-white sm:p-6">
                          <div className="grid grid-cols-6 gap-6">
                            {/* Company Information */}
                            <div className="col-span-6 ">
                              <label
                                htmlFor="companyName"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Company Name
                              </label>
                              <input
                                required
                                type="text"
                                name="companyName"
                                id="companyName"
                                value={companyName}
                                onInput={(e) => setCompanyName(e.target.value)}
                                className="mt-1 focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              />
                            </div>

                            <div className="col-span-6 ">
                              <label
                                htmlFor="companyBio"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Company Profile
                              </label>
                              <div className="mt-1">
                                <ReactQuill
                                  value={companyBio}
                                  onChange={setCompanyBio}
                                  theme="snow"
                                  placeholder="Tell candidates about your company culture, mission, and why they should join your team..."
                                />
                              </div>
                            </div>

                            {/* Job Information */}
                            <div className="col-span-6">
                              <label
                                htmlFor="title"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Job Title
                              </label>
                              <input
                                required
                                type="text"
                                name="title"
                                id="title"
                                autoComplete="title"
                                value={title}
                                onInput={(e) => setTitle(e.target.value)}
                                className="mt-1 focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              />
                            </div>

                            <div className="col-span-6 sm:col-span-2">
                              <label
                                htmlFor="category"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Category
                              </label>
                              <select
                                required
                                id="category"
                                name="category"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                              >
                                <option value="Full-time">Full-time</option>
                                <option value="Part-time">Part-time</option>
                              </select>
                            </div>

                            <div className="col-span-6 sm:col-span-2">
                              <label
                                htmlFor="salary"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Salary
                              </label>
                              <input
                                required
                                type="text"
                                name="salary"
                                id="salary"
                                placeholder="$"
                                autoComplete="salary"
                                value={salary}
                                onInput={(e) => setSalary(e.target.value)}
                                className="mt-1 focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              />
                            </div>

                            <div className="col-span-6 sm:col-span-2">
                              <label
                                htmlFor="vacancy"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Vacancies
                              </label>
                              <input
                                required
                                type="text"
                                name="vacancy"
                                id="vacancy"
                                value={vacancy}
                                onInput={(e) => setVacancy(e.target.value)}
                                className="mt-1 focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              />
                            </div>

                            {/* Location Information */}
                            <div className="col-span-6 sm:col-span-3">
                              <label
                                htmlFor="city"
                                className="block text-sm font-medium text-gray-700"
                              >
                                City
                              </label>
                              <input
                                required
                                type="text"
                                name="city"
                                id="city"
                                value={city}
                                onInput={(e) => setCity(e.target.value)}
                                className="mt-1 focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              />
                            </div>

                            <div className="col-span-6 sm:col-span-3">
                              <label
                                htmlFor="province"
                                className="block text-sm font-medium text-gray-700"
                              >
                                State / Province
                              </label>
                              <select
                                  required
                                  name="province"
                                  id="province"
                                  value={province}
                                  onChange={(e) => setProvince(e.target.value)}
                                  className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                                >
                                  <option value="" disabled>
                                    Select a province or territory
                                  </option>
                                  {PROVINCES.map((p) => (
                                    <option key={p.code} value={p.code}>
                                      {p.name}
                                    </option>
                                  ))}
                                </select>
                            </div>

                            <div className="col-span-6">
                              <label
                                htmlFor="location"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Location
                              </label>
                              <input
                                type="text"
                                name="location"
                                id="location"
                                value={location}
                                onInput={(e) => setLocation(e.target.value)}
                                className="mt-1 focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                placeholder="Various/Remote/City (Optional) "
                              />
                            </div>

                            {/* Application Details */}
                            <div className="col-span-6">
                              <label
                                htmlFor="email"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Email For Application
                              </label>
                              <input
                                required
                                type="email"
                                name="email"
                                id="email"
                                autoComplete="email"
                                value={email}
                                onInput={(e) => setEmail(e.target.value)}
                                className="mt-1 focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              />
                            </div>

                            <div className="col-span-6 ">
                              <label
                                htmlFor="about"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Job Description
                              </label>
                              <div className="mt-1">
                                <ReactQuill
                                  value={body}
                                  onChange={setBody}
                                  theme="snow"
                                  placeholder="Describe the role, responsibilities, required qualifications, and benefits..."
                                />
                              </div>
                            </div>

                            {/* Publishing Options */}
                            <fieldset className="col-span-6">
                              <legend className="text-base font-medium text-gray-900">
                                Select Job Boards
                              </legend>
                              <div className="mt-4 space-y-4">
                                <div className="flex items-start">
                                  <div className="flex items-center h-5">
                                    <input
                                      id="NewJL"
                                      name="NewJobLeads"
                                      type="checkbox"
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedBoards([...selectedBoards, 'NewJobLeads']);
                                        } else {
                                          setSelectedBoards(selectedBoards.filter(b => b !== 'NewJobLeads'));
                                        }
                                      }}
                                      className="focus:ring-red-500 h-4 w-4 text-red-600 border-gray-300 rounded"
                                    />
                                  </div>
                                  <div className="ml-3 text-sm">
                                    <label
                                      htmlFor="NewJL"
                                      className="font-medium text-gray-700"
                                    >
                                      NewJobLeads
                                    </label>
                                  </div>
                                </div>
                                <div className="flex items-start">
                                  <div className="flex items-center h-5">
                                    <input
                                      id="AboriginalJL"
                                      name="AboriginalJobLeads"
                                      type="checkbox"
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedBoards([...selectedBoards, 'AboriginalJobLeads']);
                                        } else {
                                          setSelectedBoards(selectedBoards.filter(b => b !== 'AboriginalJobLeads'));
                                        }
                                      }}
                                      className="focus:ring-red-500 h-4 w-4 text-red-600 border-gray-300 rounded"
                                    />
                                  </div>
                                  <div className="ml-3 text-sm">
                                    <label
                                      htmlFor="AboriginalJobLeads"
                                      className="font-medium text-gray-700"
                                    >
                                      AboriginalJobLeads
                                    </label>
                                  </div>
                                </div>
                                <div className="flex items-start">
                                  <div className="flex items-center h-5">
                                    <input
                                      id="YouthJL"
                                      name="YouthJobLeads"
                                      type="checkbox"
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedBoards([...selectedBoards, 'YouthJobLeads']);
                                        } else {
                                          setSelectedBoards(selectedBoards.filter(b => b !== 'YouthJobLeads'));
                                        }
                                      }}
                                      className="focus:ring-red-500 h-4 w-4 text-red-600 border-gray-300 rounded"
                                    />
                                  </div>
                                  <div className="ml-3 text-sm">
                                    <label
                                      htmlFor="YouthJL"
                                      className="font-medium text-gray-700"
                                    >
                                      YouthJobLeads
                                    </label>
                                  </div>
                                </div>
                                <div className="flex items-start">
                                  <div className="flex items-center h-5">
                                    <input
                                      id="AbleJL"
                                      name="AbleJobLeads"
                                      type="checkbox"
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedBoards([...selectedBoards, 'AbleJobLeads']);
                                        } else {
                                          setSelectedBoards(selectedBoards.filter(b => b !== 'AbleJobLeads'));
                                        }
                                      }}
                                      className="focus:ring-red-500 h-4 w-4 text-red-600 border-gray-300 rounded"
                                    />
                                  </div>
                                  <div className="ml-3 text-sm">
                                    <label
                                      htmlFor="AbleJL"
                                      className="font-medium text-gray-700"
                                    >
                                      AbleJobLeads
                                    </label>
                                  </div>
                                </div>
                                <div className="flex items-start">
                                  <div className="flex items-center h-5">
                                    <input
                                      id="RefugeeJL"
                                      name="RefugeeJobLeads"
                                      type="checkbox"
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedBoards([...selectedBoards, 'RefugeeJobLeads']);
                                        } else {
                                          setSelectedBoards(selectedBoards.filter(b => b !== 'RefugeeJobLeads'));
                                        }
                                      }}
                                      className="focus:ring-red-500 h-4 w-4 text-red-600 border-gray-300 rounded"
                                    />
                                  </div>
                                  <div className="ml-3 text-sm">
                                    <label
                                      htmlFor="RefugeeJL"
                                      className="font-medium text-gray-700"
                                    >
                                      RefugeeJobLeads
                                    </label>
                                  </div>
                                </div>

                                {/* Discount Badge */}
                                {selectedBoards.length >= 2 && (
                                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                                    <p className="text-sm text-green-800 font-medium">
                                      🎉 Multi-board discount applied! Save 10%
                                    </p>
                                  </div>
                                )}
                                {selectedBoards.length === 1 && (
                                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                                    <p className="text-sm text-red-800">
                                      💡 Select one more board to save 10%!
                                    </p>
                                  </div>
                                )}
                              </div>
                            </fieldset>

                            {/* Post Duration */}
                            <fieldset className="col-span-6 mt-6">
                              <legend className="text-base font-medium text-gray-900 mb-4">
                                Post Duration
                              </legend>
                              <div className="space-y-3">
                                <label className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:border-primary transition-colors">
                                  <div className="flex items-center">
                                    <input
                                      type="radio"
                                      name="duration"
                                      value="91"
                                      checked={duration === 91}
                                      onChange={(e) => setDuration(Number(e.target.value))}
                                      className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                                    />
                                    <span className="ml-3">
                                      <span className="block font-medium text-gray-900">91 days</span>
                                      <span className="block text-xs text-gray-500">~3 months  • Standard</span>
                                    </span>
                                  </div>
                                  <span className="font-semibold text-primary">$30/board</span>
                                </label>

                                <label className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:border-primary transition-colors">
                                  <div className="flex items-center">
                                    <input
                                      type="radio"
                                      name="duration"
                                      value="181"
                                      checked={duration === 181}
                                      onChange={(e) => setDuration(Number(e.target.value))}
                                      className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                                    />
                                    <span className="ml-3">
                                      <span className="block font-medium text-gray-900">181 days</span>
                                      <span className="block text-xs text-gray-500">~6 months • Most popular</span>
                                    </span>
                                  </div>
                                  <span className="font-semibold text-primary">$50/board</span>
                                </label>

                                <label className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:border-primary transition-colors">
                                  <div className="flex items-center">
                                    <input
                                      type="radio"
                                      name="duration"
                                      value="365"
                                      checked={duration === 365}
                                      onChange={(e) => setDuration(Number(e.target.value))}
                                      className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                                    />
                                    <span className="ml-3">
                                      <span className="block font-medium text-gray-900">365 days</span>
                                      <span className="block text-xs text-gray-500">1 year • Best Value</span>
                                    </span>
                                  </div>
                                  <span className="font-semibold text-primary">$60/board</span>
                                </label>
                              </div>
                            </fieldset>

                            {/* Pricing Summary - Desktop inline, Mobile bottom bar */}
                            <PricingSummary
                              selectedBoards={selectedBoards}
                              duration={duration}
                              legacyBalance={legacyBalance}
                            />
                          </div>
                        </div>
                      </div>
                      <button
                        disabled={loading}
                        type="submit"
                        className="bg-primary ml-4 sm:ml-0 px-4 py-2 my-4 rounded text-white text-lg flex items-center hover:bg-red-800"
                      >
                        Review & Pay
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* success state */}
        {successState && (
          <div className="flex justify-center px-4 py-16 lg:py-60">
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <CheckCircleIcon
                      className="h-6 w-6 text-primary"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Post Published Successfully.
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Your post has been submitted to our system, after it
                        passes our quality check it will show on your dashboard
                        and at the job boards you selected.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleGoToDashboard}
                  disabled={navigatingToDashboard}
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {navigatingToDashboard ? 'Loading Dashboard...' : 'Go to Dashboard'}
                </button>
                <button
                  onClick={postAnother}
                  disabled={navigatingToDashboard}
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Post Another Job
                </button>
              </div>
            </div>
          </div>
        )}

        <Toast
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          message="Unable to sign out. Please try again."
          type="error"
        />

        <Toast
          isOpen={showJobBoardToast}
          onClose={() => setShowJobBoardToast(false)}
          message="Please select at least 1 job board."
          type="error"
        />

        <Toast
          isOpen={showPaymentErrorToast}
          onClose={() => setShowPaymentErrorToast(false)}
          message="Unable to process payment. Please try again."
          type="error"
        />

        <PricingReviewModal
          isOpen={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          onProceedToPayment={handleProceedToPayment}
          jobData={{
            title,
            companyName,
            city,
            province,
            salary
          }}
          selectedBoards={selectedBoards}
          duration={duration}
          legacyBalance={legacyBalance}
        />

        {clientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <StripePaymentModal
              isOpen={showPaymentModal}
              onClose={() => setShowPaymentModal(false)}
              onSuccess={handlePaymentSuccess}
              amount={(() => {
                const PRICING = { 91: 30, 181: 50, 365: 60 };
                const pricePerBoard = PRICING[duration];
                const boardCount = selectedBoards.length;
                const subtotal = boardCount * pricePerBoard;
                const discount = boardCount >= 2 ? subtotal * 0.10 : 0;
                const afterDiscount = subtotal - discount;
                const balanceApplied = Math.min(legacyBalance, afterDiscount);
                return Math.max(0, afterDiscount - balanceApplied);
              })()}
              clientSecret={clientSecret}
              userEmail={currentUser?.email}
            />
          </Elements>
        )}

        <Footer />
      </>
    );
  }
}

export default Postjob;
